// Scanner automatique des dossiers et fichiers de services
// Scanne réellement le système de fichiers via import.meta.glob

// Fonction pour extraire le nom de fichier sans extension
const getFileKey = (path: string): string => {
  const parts = path.split('/');
  const fileName = parts[parts.length - 1];
  return fileName.replace(/\.(tsx|ts|jsx|js)$/, '');
};

// Fonction pour extraire le nom du dossier
const getFolderName = (path: string): string => {
  const parts = path.split('/');
  const servicesIndex = parts.findIndex(p => p === 'services');
  return parts[servicesIndex + 1] || '';
};

// Fonction pour générer une route par défaut à partir du nom de fichier
const generateDefaultRoute = (fileKey: string): string => {
  return fileKey
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '');
};

// Fonction pour générer un label lisible
const generateLabel = (fileKey: string): string => {
  return fileKey
    .replace(/([A-Z])/g, ' $1')
    .trim();
};

export interface ServiceFile {
  key: string;
  fileName: string;
  defaultRoute: string;
  label: string;
  path: string;
}

export interface ServiceFolder {
  name: string;
  label: string;
  files: ServiceFile[];
}

// Scanner automatique des fichiers de services
export const scanServiceFolders = (): ServiceFolder[] => {
  // Utiliser import.meta.glob pour scanner tous les fichiers dans services/
  const serviceModules = import.meta.glob('/src/pages/services/**/*.{tsx,ts,jsx,js}');
  
  const folderMap = new Map<string, ServiceFile[]>();

  // Parcourir tous les fichiers trouvés
  Object.keys(serviceModules).forEach(path => {
    const folderName = getFolderName(path);
    const fileKey = getFileKey(path);
    const fileName = path.split('/').pop() || '';

    if (!folderName || !fileKey) return;

    const serviceFile: ServiceFile = {
      key: fileKey,
      fileName: fileName,
      defaultRoute: generateDefaultRoute(fileKey),
      label: generateLabel(fileKey),
      path: path
    };

    if (!folderMap.has(folderName)) {
      folderMap.set(folderName, []);
    }
    folderMap.get(folderName)!.push(serviceFile);
  });

  // Convertir en tableau de ServiceFolder
  const folders: ServiceFolder[] = [];
  folderMap.forEach((files, name) => {
    folders.push({
      name,
      label: name.charAt(0).toUpperCase() + name.slice(1),
      files: files.sort((a, b) => a.label.localeCompare(b.label))
    });
  });

  return folders.sort((a, b) => a.name.localeCompare(b.name));
};

// Fonction pour obtenir les fichiers d'un dossier spécifique
export const getServiceFiles = (folderName: string): ServiceFile[] => {
  const folders = scanServiceFolders();
  const folder = folders.find(f => f.name === folderName);
  return folder?.files || [];
};

// Fonction pour obtenir tous les dossiers disponibles
export const getAvailableFolders = (): ServiceFolder[] => {
  return scanServiceFolders();
};

// Fonction pour valider qu'une route est unique
export const isRouteUnique = (route: string, currentKey: string, routes: Record<string, string>): boolean => {
  return !Object.entries(routes).some(([key, r]) => key !== currentKey && r === route);
};

// Fonction pour obtenir le mapping des imports dynamiques
export const getServiceImports = (): Record<string, Record<string, () => Promise<any>>> => {
  const serviceModules = import.meta.glob('/src/pages/services/**/*.{tsx,ts,jsx,js}');
  const importMap: Record<string, Record<string, () => Promise<any>>> = {};

  Object.entries(serviceModules).forEach(([path, importFn]) => {
    const folderName = getFolderName(path);
    const fileKey = getFileKey(path);

    if (!folderName || !fileKey) return;

    if (!importMap[folderName]) {
      importMap[folderName] = {};
    }
    importMap[folderName][fileKey] = importFn as () => Promise<any>;
  });

  return importMap;
};
